import { Box, BoxProps, ButtonNew, EmptyView, ModBox, Spacer, Text, useWindowDimensions } from '@jfs/ui-new'
import { getErrorMessage, getRandomAlphaNumString } from '@jfs/utils'
import { useIsFocused, useNavigation } from '@react-navigation/core'
import { ReactElement, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { Dimensions } from 'react-native'
import { CalendarTypes } from '../../comps/Calendar/types'
import CalendarViewModal from '../../comps/CalendarViewModal'
import Tabbar, { TabbarType, TabData } from '../../comps/Tabbar'
import { transactionScreenStrings } from '../../data/static/strings'
import { useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { calendarWebWidth, getRangeDate } from '../../util/util'
import { TitleSubTitleLayout } from './TitleSubTitleLayout'

const getList = (data: any) => {
  return data?.map((item: any, index: number) => {
    return (
      <TitleSubTitleLayout
        placeHolder={item?.label}
        title={item?.value}
        tooltip={item?.tooltip}
        top={index === 0 || index === 3 ? 10 : 0}
        key={getRandomAlphaNumString(index + 1)}
      />
    )
  })
}
const stats = [
  { title: 'Today' },
  { title: 'This Week' },
  { title: 'This Month' },
  { title: 'Custom Dates', iconId: 'CALENDAR' },
]

const CampaignHighlights = () => {
  const [visible, setVisible] = useState(false)
  const [tabbarData, setTabbarData] = useState<TabData[]>(stats)
  const [layoutPos, setLayoutPos] = useState<any>('')
  const [calendarLayout, setCalendarLayout] = useState<any>('')
  const windowWidth = Dimensions.get('window').width
  const calendarLayoutWdith = calendarLayout?.layout?.width
  const remainingWidth = windowWidth - calendarLayoutWdith
  const isWebCalendarOpenInNative = windowWidth - calendarWebWidth < 200
  const [calenderPos, setCalenderPos] = useState<any>({ left: undefined, top: undefined, right: undefined })
  const {
    dateTabIndex,
    setDateTabIndex,
    setNewDate,
    date,
    dateLabel,
    setMessage,
    fetchCampaignsSummary,
    mid,
    summary,
    setAttributesCount,
    resetProducts,
  } = useStores((store) => ({
    dateTabIndex: store?.settings?.metaData.dateTabIndex,
    setDateTabIndex: store?.settings?.metaData.setDateTabIndex,
    mid: store?.user?.selectedMid,
    date: store?.campaigns?.selectedDateInfo,
    setNewDate: store?.campaigns?.updateSelectedDateInfo,
    setMessage: store?.response?.setMessage,
    fetchCampaignsSummary: store.campaigns.fetchCampaignsSummary,
    dateLabel: store?.campaigns?.selectedDateInfo?.dateLabel,
    summary: store.campaigns.getSummary(),
    setAttributesCount: store.campaigns.updateTxnAttributesCount,
    resetProducts: store.campaigns.resetProducts,
  }))

  const [fetchSummaryApi, summaryApiHandler] = useLazyApiData(fetchCampaignsSummary)
  const isFocused = useIsFocused()
  const { navigate } = useNavigation()
  useEffect(() => {
    if (mid && isFocused) {
      fetchSummaryApi()
    }
  }, [mid, fetchSummaryApi, mid, isFocused, dateTabIndex, setNewDate, dateLabel, date.fromDate, date.toDate])

  const handleRetry = () => {
    if (mid && isFocused) {
      fetchSummaryApi()
    }
  }

  const onCalendarLayout = (nativeEventData: any) => {
    if (calendarLayout === '') {
      setCalendarLayout(nativeEventData?.nativeEvent)
      layoutPos.target?.measure((...args: any) => {
        const left = !isWebCalendarOpenInNative ? args[4] - 170 - args[0] : undefined

        const top = args[5] - args[1] + 15
        const right = remainingWidth < calendarLayoutWdith || isWebCalendarOpenInNative ? 17 : undefined
        setCalenderPos({
          left,
          top,
          right,
        })
      })
    }
  }

  const onDateSelected = (firstdate: Date, secondDate?: Date): void => {
    if (secondDate) {
      updateCustomDateString(firstdate, secondDate)
      setVisible(false)
      setNewDate({
        fromDate: firstdate,
        toDate: secondDate,
        dateLabel: 'custom',
      })
      setDateTabIndex(dateTabIndex < 3 ? 3 : dateTabIndex + 1)
    }
  }

  const updateCustomDateString = (fromDate: any, toDate: any) => {
    const dateString: any = getRangeDate(fromDate, toDate)
    const updatedStatistics = stats.slice()
    const items: TabData[] = []
    updatedStatistics.map((item) => {
      if (item.title === 'Custom Dates') {
        items.push({ title: dateString, iconId: item.iconId })
      } else {
        items.push(item)
      }
      return item
    })
    setTabbarData(items)
  }

  const renderCalendarModal = (): ReactElement => {
    return (
      <ModBox {...styles(viewPortWidth).modal} show={visible}>
        <Box
          onPress={close}
          testID="calender-modal"
          underlayColor={'TRANSPARENT'}
          {...styles(viewPortWidth).overlayStyle}>
          <Box
            onLayout={onCalendarLayout}
            {...styles(viewPortWidth).dropdownStyle(calenderPos.left, calenderPos.top, calenderPos.right)}
            backgroundColor="AVAILABLE_GREEN">
            <CalendarViewModal
              onDateSelection={onDateSelected}
              dateRange={getRangeDate(date.fromDate, date.toDate)}
              type={CalendarTypes.HISTORY}
              onCancel={close}
              maxDuration={31}
              sendError={(err: string) => setMessage(err)}
            />
          </Box>
        </Box>
      </ModBox>
    )
  }

  const renderPortal = (): React.ReactPortal => {
    return ReactDOM.createPortal(renderCalendarModal(), document.body)
  }

  const viewPortWidth = useWindowDimensions()

  const themedStyle = styles(viewPortWidth)

  const getDateValue = (dateValue: number): Date => {
    const today = new Date(Date.now())
    return new Date(today.setDate(today.getDate() - dateValue))
  }

  const getTodayValue = (): Date => {
    const today = new Date(Date.now())
    return new Date(today.setHours(0, 0, 0, 0))
  }

  const getSelectedDate = (type: number): Date => {
    const getSelectedValue: any = {
      0: getTodayValue(),
      1: getDateValue(7),
      2: getDateValue(30),
    }
    return getSelectedValue[type]
  }

  const handleSelected = (index: number): void => {
    if (index < 3) {
      setDateTabIndex(index)
      const today = new Date(Date.now())
      const currentDay = today
      const finishDay = getSelectedDate(index)
      setNewDate({
        fromDate: finishDay,
        toDate: currentDay,
        dateLabel: 'specific',
      })
    } else {
      setTimeout(() => {
        setVisible(true)
      }, 300)
    }
  }

  const onLayout = (nativeEventData: any) => {
    setLayoutPos(nativeEventData?.nativeEvent)
    if (!calenderPos.top) {
      nativeEventData?.nativeEvent?.target?.measure((...args: any) => {
        const top = args[5] - args[1] + 15
        const left = args[4] - 170 - args[0]
        setCalenderPos({
          ...calenderPos,
          left,
          top,
        })
      })
    }
  }

  const errorState = getErrorMessage([summaryApiHandler])
  return (
    <Box {...themedStyle.container}>
      {renderPortal()}
      <Box flex={0.8}>
        <Box {...themedStyle.titleContainer}>
          <Text testID="homePageTitle" fontFamily="jiotype-medium" fontSize={20} color={'BLACK_21'}>
            {'Campaigns'}
          </Text>
          <Spacer direction={'vertical'} size={8} />
          <Box {...themedStyle.exportRow}>
            <Tabbar
              default={dateTabIndex > 3 ? 3 : dateTabIndex}
              titles={tabbarData}
              onSelected={handleSelected}
              type={TabbarType.highlights}
              onLayoutOnlyForPosition={3}
              onLayout={onLayout}
            />
            <Spacer direction={'horizontal'} size={16} />
          </Box>
        </Box>
        <Spacer size={24} direction={'vertical'} />
        <ModBox show={summaryApiHandler?.state === 'fulfilled'} {...themedStyle.flexRow}>
          {summary && getList(summary)}
        </ModBox>
        <ModBox show={summaryApiHandler?.state !== 'fulfilled'} h={80} width="100%">
          <EmptyView
            onRetry={handleRetry}
            isLoading={summaryApiHandler?.state === 'pending'}
            error={errorState}
            emptyMessage={transactionScreenStrings.empty}
            loadingMessage={transactionScreenStrings.loading}
            hide={summaryApiHandler?.state === 'fulfilled'}
          />
        </ModBox>
      </Box>
      <Box w={'ONE'} bg={'GREY_SEPARATOR'} />
      <Box align={'center'} justify={'center'} f={0.2} pl={16} minWidth={140}>
        <ButtonNew
          onPress={() => [setAttributesCount(0), resetProducts(), navigate(Routes.CREATE_CAMPAIGN)]}
          title={'Create Campaign'}
          hoverColor={'PRIMARY'}
          underlayColor={'PRIMARY'}
          borderRadius={8}
          isLoading={false}
          height={48}
          width={180}
        />
      </Box>
    </Box>
  )
}

const styles = (breakpoint: number) => {
  let overRideFlex: BoxProps = {}
  let overRideTab: BoxProps = {}
  let overRideFlexRow: BoxProps = {}
  let overrideExportRow: BoxProps = {}
  let overrideTitleContainer: BoxProps = {}

  const shadowStyle = {
    shadowOffset: { width: 1, height: 1 },
    shadowRadius: 3,
    shadowColor: '#0000001F',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#00000014',
    zIndex: 10,
  }

  if (breakpoint < 1032) {
    overrideExportRow = {
      flexDirection: 'column',
      justifyContent: 'space-between',
      flexGrow: 1,
    } as BoxProps
    overrideTitleContainer = {
      flexDirection: 'column',
      align: 'flex-start',
    } as BoxProps
  }
  if (breakpoint < 1000) {
    overRideFlex = {
      flexDirection: 'column',
      alignItems: 'flex-start',
    } as BoxProps
    overRideTab = {
      marginTop: 15,
    } as BoxProps
  }

  if (breakpoint < 600 && breakpoint > 460) {
    overRideFlexRow = {
      justifyContent: 'flex-start',
    } as BoxProps
  }

  if (breakpoint < 380) {
    overRideTab = {
      marginTop: 15,
      width: 300,
    } as BoxProps
  }
  return {
    container: {
      ...shadowStyle,
      padding: 16,
      minHeight: 5,
      backgroundColor: 'WHITE',
      paddingBottom: 10,
      marginHorizontal: 16,
      ph: 16,
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 16,
    } as BoxProps,
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'WHITE',
      justifyContent: 'space-between',
      marginTop: 16,
      ...overRideFlex,
    } as BoxProps,
    textContainer: {
      flexDirection: 'row',
      backgroundColor: 'WHITE',
    } as BoxProps,
    tabbar: {
      justifyContent: 'flex-start',
      backgroundColor: 'WHITE',
      ...overRideTab,
    } as BoxProps,
    titleContainer: {
      flexDirection: 'row',
      align: 'center',
      justify: 'space-between',
      flexWrap: 'wrap',
      ...overrideTitleContainer,
    } as BoxProps,
    exportRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
      ...overrideExportRow,
    } as BoxProps,
    listStyle: { flex: 1, justifyContent: 'space-between' } as BoxProps,
    flexRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      ...overRideFlexRow,
    } as BoxProps,
    modal: {
      position: 'absolute',
      elevation: 2,
      shadowOpacity: 1,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      ...shadowStyle,
      zIndex: 1,
      borderWidth: 1,
      borderColor: 'GRAY_BORDER',
    } as BoxProps,
    overlayStyle: {
      backgroundColor: 'TRANSPARENT',
      width: '100%',
      height: '100%',
      justifyContent: 'center',
    } as BoxProps,
    dropdownStyle: (XAxisPos: number | undefined, yAxisPos: number | undefined, rightPos: number | undefined) =>
    ({
      position: 'absolute',
      flex: 1,
      shadowColor: 'BLACK_LIGHT',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.3,
      shadowRadius: 10,
      elevation: 2,
      left: XAxisPos,
      top: yAxisPos ? yAxisPos + 15 : undefined,
      borderRadius: 12,
      backgroundColor: 'BLUE',
      borderColor: 'SECONDARY_GREY',
      alignSelf: 'center',
      right: rightPos,
    } as BoxProps),
  }
}

export { CampaignHighlights }
